import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { testService, adminService, authService } from '../services/api';
import { toast } from 'sonner';
import { Search, Filter, ArrowUpDown, Star, History as HistoryIcon, Download, Pencil, Trash2, X } from 'lucide-react';
import { AutocompleteInput } from '../components/AutocompleteInput';
import { Pagination } from '../components/Pagination';
import { API_BASE } from '../services/api';

export const HistoriquePage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const [editingTest, setEditingTest] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 15;
  const [filters, setFilters] = useState({
    fonction: '',
    niveau: '',
    client: '',
    evaluateur: ''
  });

  useEffect(() => {
    fetchTests();
  }, [search, sortBy, filters, page]);

  const fetchTests = async () => {
    try {
      const response = await testService.getAll({
        search,
        sort_by: sortBy,
        page,
        page_size: pageSize,
        ...filters
      });
      const data = response.data;
      if (data.items) {
        setTests(data.items);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      } else {
        setTests(data);
        setTotal(data.length);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Reset page when filters/search change
  const handleFilterChange = (newFilters) => {
    setPage(1);
    setFilters(newFilters);
  };
  const handleSearchChange = (val) => {
    setPage(1);
    setSearch(val);
  };
  const handleSortChange = (val) => {
    setPage(1);
    setSortBy(val);
  };

  const handleExportCsv = () => {
    const url = `${API_BASE}/tests/export-csv`;
    fetch(url, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', 'historique_tests.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Export CSV téléchargé');
      })
      .catch(() => toast.error('Erreur lors de l\'export'));
  };

  const handleEditTest = (e, test) => {
    e.preventDefault();
    e.stopPropagation();
    setEditForm({
      client: test.questionnaire?.client || '',
      ref_ao: test.questionnaire?.ref_ao || '',
      nom_candidat: test.nom_candidat || '',
      prenom_candidat: test.prenom_candidat || '',
    });
    setEditingTest(test);
  };

  const handleSaveEdit = async () => {
    try {
      await adminService.updateTest(editingTest.id, editForm);
      toast.success('Test mis à jour');
      setEditingTest(null);
      fetchTests();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTest = async (testId) => {
    try {
      await adminService.deleteTest(testId);
      toast.success('Test supprimé');
      setDeleteConfirm(null);
      fetchTests();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Layout title="Historique des tests" breadcrumbs={['Accueil', 'Historique']}>
      {/* Search, Sort and Export */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Rechercher un candidat, poste, client..."
            className="eva-input pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-3">
          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            className="eva-input flex-1"
            data-testid="sort-select"
          >
            <option value="date">Trier par date (récent)</option>
            <option value="score">Trier par score</option>
            <option value="candidat">Trier par candidat</option>
          </select>
          <button
            onClick={handleExportCsv}
            className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-lg text-sm font-medium text-white hover:bg-white/5 transition-colors whitespace-nowrap"
            data-testid="export-csv-button"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="eva-card mb-6" data-testid="filters">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={18} className="text-slate-400" />
          <h3 className="font-semibold text-white">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="eva-label">Poste</label>
            <AutocompleteInput
              value={filters.fonction}
              onChange={(val) => handleFilterChange({ ...filters, fonction: val })}
              endpoint="/autocomplete/fonctions"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-fonction"
            />
          </div>
          <div>
            <label className="eva-label">Niveau</label>
            <select
              value={filters.niveau}
              onChange={(e) => handleFilterChange({ ...filters, niveau: e.target.value })}
              className="eva-input"
              data-testid="filter-niveau"
            >
              <option value="">Tous</option>
              <option value="Junior">Junior</option>
              <option value="Confirmé">Confirmé</option>
              <option value="Senior">Senior</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div>
            <label className="eva-label">Client</label>
            <AutocompleteInput
              value={filters.client}
              onChange={(val) => handleFilterChange({ ...filters, client: val })}
              endpoint="/autocomplete/entreprises"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-client"
            />
          </div>
          <div>
            <label className="eva-label">Évaluateur</label>
            <AutocompleteInput
              value={filters.evaluateur}
              onChange={(val) => handleFilterChange({ ...filters, evaluateur: val })}
              endpoint="/autocomplete/evaluateurs"
              className="eva-input"
              placeholder="Tous"
              dataTestId="filter-evaluateur"
            />
          </div>
        </div>
      </div>

      {/* Tests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      ) : tests.length === 0 ? (
        <div className="text-center py-12" data-testid="empty-state">
          <HistoryIcon size={48} className="text-[#E9ECEF] mx-auto mb-4" />
          <p className="text-slate-400">Aucun test trouvé</p>
        </div>
      ) : (
        <div className="eva-card" data-testid="tests-list">
          <div className="space-y-0">
            {tests.map((test, index) => (
              <div
                key={test.id}
                className={`relative group ${
                  index !== tests.length - 1 ? 'border-b border-white/10' : ''
                }`}
              >
                <Link
                  to={`/rapport/${test.id}`}
                  className="block p-4 hover:bg-white/5 transition-colors"
                  data-testid={`test-${test.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <h3 className="font-semibold text-white mb-1">
                        {test.questionnaire?.client || 'Test générique'}
                        {test.questionnaire?.ref_ao && (
                          <span className="text-slate-400 font-normal"> — {test.questionnaire.ref_ao}</span>
                        )}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span>{test.prenom_candidat} {test.nom_candidat}</span>
                        <span>•</span>
                        <span>{test.questionnaire?.fonction || ''}</span>
                        <span>•</span>
                        <span>{new Date(test.date).toLocaleDateString('fr-FR')}</span>
                        {test.evaluateur && (
                          <>
                            <span>•</span>
                            <span className="italic">par {test.evaluateur}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          {[...Array(10)].map((_, i) => (
                            <Star
                              key={i}
                              size={12}
                              fill={i < Math.round(test.average_score || 0) ? '#F59E0B' : '#1E293B'}
                              stroke={i < Math.round(test.average_score || 0) ? '#F59E0B' : '#1E293B'}
                            />
                          ))}
                        </div>
                        <p className="text-sm font-medium text-white">
                          {test.average_score?.toFixed(1)}/10
                        </p>
                        {test.evaluation && (
                          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                            test.evaluation === 'Très bon niveau' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
                            test.evaluation === 'Au-dessus de la moyenne' ? 'bg-[#10B981] bg-opacity-15 text-[#10B981]' :
                            test.evaluation === 'Dans la moyenne' ? 'bg-[#F59E0B] bg-opacity-15 text-[#F59E0B]' :
                            'bg-[#EF4444] bg-opacity-15 text-[#EF4444]'
                          }`}>{test.evaluation}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
                {isAdmin && (
                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleEditTest(e, test)}
                      className="p-1.5 bg-[#0B132B] border border-white/10 rounded-md hover:bg-white/5 transition-colors"
                      title="Modifier"
                      data-testid={`edit-test-${test.id}`}
                    >
                      <Pencil size={14} className="text-slate-400" />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteConfirm(test); }}
                      className="p-1.5 bg-[#0B132B] border border-white/10 rounded-md hover:bg-red-50 transition-colors"
                      title="Supprimer"
                      data-testid={`delete-test-${test.id}`}
                    >
                      <Trash2 size={14} className="text-[#EF4444]" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && tests.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      {/* Edit Modal */}
      {editingTest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-test-modal">
          <div className="bg-[#0B132B] rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Modifier le test</h3>
              <button onClick={() => setEditingTest(null)} className="p-1 hover:bg-white/5 rounded">
                <X size={20} className="text-slate-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="eva-label">Client</label>
                <input
                  type="text"
                  value={editForm.client}
                  onChange={(e) => setEditForm({ ...editForm, client: e.target.value })}
                  className="eva-input"
                  data-testid="edit-client-input"
                />
              </div>
              <div>
                <label className="eva-label">Réf. AO</label>
                <input
                  type="text"
                  value={editForm.ref_ao}
                  onChange={(e) => setEditForm({ ...editForm, ref_ao: e.target.value })}
                  className="eva-input"
                  data-testid="edit-ref-ao-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="eva-label">Prénom candidat</label>
                  <input
                    type="text"
                    value={editForm.prenom_candidat}
                    onChange={(e) => setEditForm({ ...editForm, prenom_candidat: e.target.value })}
                    className="eva-input"
                    data-testid="edit-prenom-input"
                  />
                </div>
                <div>
                  <label className="eva-label">Nom candidat</label>
                  <input
                    type="text"
                    value={editForm.nom_candidat}
                    onChange={(e) => setEditForm({ ...editForm, nom_candidat: e.target.value })}
                    className="eva-input"
                    data-testid="edit-nom-input"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingTest(null)} className="eva-btn-secondary">Annuler</button>
              <button onClick={handleSaveEdit} className="eva-btn-primary" data-testid="save-edit-button">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="delete-test-modal">
          <div className="bg-[#0B132B] rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-slate-400 mb-6">
              Voulez-vous vraiment supprimer le test de <strong>{deleteConfirm.prenom_candidat} {deleteConfirm.nom_candidat}</strong> ?
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="eva-btn-secondary">Annuler</button>
              <button
                onClick={() => handleDeleteTest(deleteConfirm.id)}
                className="px-4 py-2 bg-[#EF4444] text-white rounded-lg text-sm font-medium hover:bg-[#DC2626] transition-colors"
                data-testid="confirm-delete-button"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
